// Bookings: calls booked on Cal.com, matched to their Airtable record, and the
// one button that writes the match back through the engine's own script.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../services";
import type { Booking, BookingsBoard, Defined, Job, RunResponse } from "../services/types";
import { PageHead, SectionHead } from "../components/dash/PageHead";
import { StatTiles } from "../components/dash/StatTiles";
import { DefinitionsPanel } from "../components/dash/DefinitionsPanel";
import { DataTable, type Column } from "../components/dash/DataTable";
import { ConfirmBar } from "../components/dash/ConfirmBar";
import { RunResults, useJobRunner } from "../components/dash/JobPanel";
import { formatIST, formatISTDateTime } from "../lib/readAgo";

const TILES = ["bookings.upcoming", "bookings.matched", "bookings.unmatched", "bookings.needs_row"];

const COLUMNS: Column<Booking>[] = [
  { key: "when", label: "When IST", cell: (b) => formatISTDateTime(b.start) },
  {
    key: "guest", label: "Guest",
    cell: (b) => <>{b.attendee?.name || ""}{b.cancelled ? <span className="dim">cancelled</span> : null}</>,
  },
  { key: "show", label: "Show", cell: (b) => b.match?.channel || "" },
  { key: "email", label: "Email", cell: (b) => b.attendee?.email || "" },
  {
    key: "airtable", label: "Airtable",
    cell: (b) => (
      <>
        {b.match ? "matched" : "not found"}
        {b.needs?.length ? <span className="dim">{b.needs.join(", ")}</span> : null}
      </>
    ),
  },
  {
    key: "open", label: "Open in Cal.com",
    cell: (b) => (
      <a href={`https://app.cal.com/booking/${encodeURIComponent(b.uid)}`} target="_blank" rel="noreferrer">
        Open in Cal.com
      </a>
    ),
  },
];

const JOB_COLUMNS = (open: (id: number) => void): Column<Job>[] => [
  { key: "started", label: "Started IST", cell: (j) => formatIST(j.started_at || j.created_at) },
  { key: "status", label: "Status", cell: (j) => j.status },
  { key: "by", label: "Requested by", cell: (j) => j.requested_by || "" },
  {
    key: "summary", label: "Summary",
    cell: (j) => (typeof j.summary === "string" ? j.summary : j.summary ? JSON.stringify(j.summary) : ""),
  },
  {
    key: "log", label: "Open log",
    cell: (j) => <button type="button" className="site" onClick={() => open(j.id)}>Open log</button>,
  },
];

function needsList(r: RunResponse | null, board: BookingsBoard | undefined): string[] {
  const fromTool = r?.needs ?? r?.changes;
  if (Array.isArray(fromTool) && fromTool.length) {
    return fromTool.map((x) => (typeof x === "string" ? x : JSON.stringify(x)));
  }
  return (board?.upcoming ?? [])
    .filter((b) => b.needs?.length)
    .map((b) => `${b.attendee?.name || b.attendee?.email || b.uid}: ${b.needs.join(", ")}`);
}

export function Bookings() {
  const runner = useJobRunner();
  const [preview, setPreview] = useState<RunResponse | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  const boardQ = useQuery({
    queryKey: ["bookings:board"],
    queryFn: async () => {
      const r = await api.bookings.board();
      if (!r.ok) throw new Error(r.error);
      return r as Defined<BookingsBoard>;
    },
    staleTime: 120_000,
  });

  const jobsQ = useQuery({
    queryKey: ["jobs", "bookings_sync"],
    queryFn: async () => {
      const r = await api.jobs.list("bookings_sync", undefined, 10);
      if (!r.ok) throw new Error(r.error);
      return r.jobs ?? [];
    },
    staleTime: 30_000,
  });

  async function previewSync() {
    setPreview(null);
    setPreviewErr(null);
    const r = await api.bookings.sync(false);
    if (!r.ok) setPreviewErr(r.error);
    else setPreview(r);
  }

  function refetchAll() {
    void boardQ.refetch();
    void jobsQ.refetch();
  }

  const d = boardQ.data;
  const s = d?.summary;
  const needs = needsList(preview, d);

  return (
    <>
      <PageHead title="Bookings" caption="Calls booked on Cal.com, matched to the prospect record in Airtable." />

      <section className="section compact">
        <SectionHead title="Today" />
        {boardQ.isError ? (
          <div className="note-neg"><p>{(boardQ.error as Error).message}</p></div>
        ) : !d ? (
          <p className="empty">Reading Cal.com.</p>
        ) : (
          <StatTiles
            tiles={[
              { id: "bookings.upcoming", value: s?.upcoming },
              { id: "bookings.matched", value: s?.matched },
              { id: "bookings.unmatched", value: s?.unmatched },
              { id: "bookings.needs_row", value: s?.needs_row },
            ]}
            definitions={d.definitions}
            readAt={d.read_at}
          />
        )}
      </section>

      <section className="section compact">
        <SectionHead title="Run" />
        <div className="run">
          <button type="button" className="btn quiet" disabled={boardQ.isFetching} onClick={() => void boardQ.refetch()}>
            {boardQ.isFetching ? "Reading" : "Refresh board"}
          </button>
          <ConfirmBar
            label="Check Cal.com now"
            confirmLabel="Yes, write to Airtable"
            note="Runs calcom_watch_run.py with --write: sets Status to meeting booked and creates or corrects Bookings rows. Cancellations are only flagged."
            disabled={!!runner.busy || !d}
            busy={runner.busy === "Check Cal.com"}
            onArm={previewSync}
            onCancel={() => setPreview(null)}
            onConfirm={() => runner.run("Check Cal.com", () => api.bookings.sync(true))}
          >
            {previewErr ? (
              <div className="note-neg"><p>{previewErr}</p></div>
            ) : (
              <>
                <p>{preview?.message || (preview ? "This is what the write would change." : "Reading what the write would change.")}</p>
                {needs.length ? (
                  <ul>{needs.map((n, i) => <li key={i}>{n}</li>)}</ul>
                ) : preview ? (
                  <p>Nothing needs changing.</p>
                ) : null}
              </>
            )}
          </ConfirmBar>
        </div>
        <RunResults runner={runner} onFinish={refetchAll} />
      </section>

      <section className="section compact">
        <SectionHead title="Upcoming" />
        {boardQ.isError ? null : (
          <DataTable
            columns={COLUMNS}
            rows={d?.upcoming ?? []}
            rowKey={(b) => b.uid}
            loading={boardQ.isPending}
            loadingText="Reading Cal.com."
            empty="No bookings in the next 30 days."
          />
        )}
      </section>

      <section className="section compact">
        <SectionHead title="Recent checks" />
        {jobsQ.isError ? (
          <div className="note-neg"><p>{(jobsQ.error as Error).message}</p></div>
        ) : (
          <DataTable
            columns={JOB_COLUMNS((id) => runner.setJobId(id))}
            rows={jobsQ.data ?? []}
            rowKey={(j) => String(j.id)}
            loading={jobsQ.isPending}
            loadingText="Reading the job list."
            empty="No jobs have run."
          />
        )}
      </section>

      {d ? <DefinitionsPanel ids={TILES} definitions={d.definitions} readAt={d.read_at} /> : null}
    </>
  );
}
